import { Component, OnInit, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { EMPTY } from 'rxjs';
import { expand, finalize, map, reduce, tap } from 'rxjs/operators';
import {
  productTypeLabel,
  subscriptionEventBadgeClass,
  subscriptionEventLabel,
  SubscriptionEvent,
  SubscriptionInvoiceReport,
  SubscriptionProductType,
} from '../../../model/v3/subscriptions';
import { V3Person } from '../../../model/v3/people';
import { ManagerUtils } from '../../../services/utils/v3/manager-utils';
import { SubscriptionsV3RestService } from '../../../services/rest/v3/subscriptions-rest.service';
import { ExportExcelService } from '../../../services/export-excel.service';
import { Page, Pageable } from '../../../model/rest/base';
import { ModalComponent } from '../../../components/modal/modal.component';

@Component({
  selector: 'app-admin-subscriptions-page',
  templateUrl: './admin-subscriptions-page.component.html',
  standalone: false,
  styleUrls: ['./admin-subscriptions-page.component.scss'],
})
export class AdminSubscriptionsPageComponent implements OnInit {
  month: string;
  reports: SubscriptionInvoiceReport[] = [];
  totals: SubscriptionInvoiceReport | null = null;
  loading = false;

  invoicePage: Page<SubscriptionInvoiceReport> | null = null;
  invoicePageable = Pageable.of(0, 25, null);

  auditSchool: SubscriptionInvoiceReport | null = null;
  auditPage: Page<SubscriptionEvent> | null = null;
  auditPageable = Pageable.of(0, 25, null);
  auditLoading = false;
  @ViewChild('auditModal', { static: true })
  auditModal: ModalComponent;

  constructor(
    private subscriptionsRest: SubscriptionsV3RestService,
    private exportExcel: ExportExcelService,
  ) {}

  ngOnInit(): void {
    this.month = new Date().toISOString().substring(0, 7);
    this.load();
  }

  load() {
    if (!this.month) {
      return;
    }
    this.loading = true;
    this.subscriptionsRest
      .getInvoiceReports(this.month)
      .pipe(
        tap((reports) => {
          this.reports = reports;
          this.totals = this.sumUp(reports);
          this.invoicePageable = Pageable.of(0, this.invoicePageable.size, null);
          this.invoicePage = this.buildInvoicePage();
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe();
  }

  showAudit(report: SubscriptionInvoiceReport) {
    this.auditPageable = Pageable.of(0, 25, null);
    this.auditSchool = report;
    this.auditPage = null;
    this.auditModal.show();
    this.loadAudit();
  }

  switchAuditPage(pageable: Pageable) {
    this.auditPageable = pageable;
    this.loadAudit();
  }

  private loadAudit() {
    this.auditLoading = true;
    this.subscriptionsRest
      .getAuditReport(this.month, this.auditSchool.schoolId, this.auditPageable)
      .pipe(
        tap((page) => (this.auditPage = page)),
        finalize(() => (this.auditLoading = false)),
      )
      .subscribe();
  }

  productLabel(productType: SubscriptionProductType): string {
    return productTypeLabel(productType);
  }

  personName(person: V3Person): string {
    return ManagerUtils.getName(person);
  }

  eventBadgeClass(event: SubscriptionEvent): string {
    return subscriptionEventBadgeClass(event.eventType);
  }

  eventLabel(event: SubscriptionEvent): string {
    return subscriptionEventLabel(event);
  }

  switchInvoicePage(pageable: Pageable) {
    this.invoicePageable = pageable;
    this.invoicePage = this.buildInvoicePage();
  }

  /**
   * The generic report arrives whole, so the invoice table pages client-side.
   */
  private buildInvoicePage(): Page<SubscriptionInvoiceReport> {
    const size = this.invoicePageable.size;
    const number = this.invoicePageable.page;
    const page = new Page<SubscriptionInvoiceReport>();
    page.content = this.reports.slice(number * size, (number + 1) * size);
    page.totalElements = this.reports.length;
    page.totalPages = Math.max(1, Math.ceil(this.reports.length / size));
    page.size = size;
    page.number = number;
    page.numberOfElements = page.content.length;
    page.first = number === 0;
    page.last = number >= page.totalPages - 1;
    page.empty = !page.content.length;
    return page;
  }

  exportInvoice() {
    if (!this.reports.length) {
      return;
    }
    const rows = [...this.reports, this.totals].filter(Boolean).map((report) => ({
      school: report.schoolName,
      'Student registrations - new': report.newStudentRegistrations,
      'Student registrations - manual renewals': report.manualRenewedStudentRegistrations,
      'Student registrations - auto renewals': report.automaticRenewedStudentRegistrations,
      'Student registrations - payable': report.payableStudentRegistrations,
      'Teacher registrations - new': report.newTeacherRegistrations,
      'Teacher registrations - manual renewals': report.manualRenewedTeacherRegistrations,
      'Teacher registrations - auto renewals': report.automaticRenewedTeacherRegistrations,
      'Teacher registrations - payable': report.payableTeacherRegistrations,
      'Teacher subscriptions - new': report.newTeacherSubscriptions,
      'Teacher subscriptions - manual renewals': report.manualRenewedTeacherSubscriptions,
      'Teacher subscriptions - auto renewals': report.automaticRenewedTeacherSubscriptions,
      'Teacher subscriptions - payable': report.payableTeacherSubscriptions,
      'payable students': report.payableStudents,
      'payable teachers': report.payableTeachers,
      stopped: report.stoppedSubscriptions,
      expired: report.expiredSubscriptions,
      totalPayable: report.totalPayable,
    }));
    this.exportExcel.exportToExcel(rows, `subscriptions-invoice-${this.month}`, 'default');
  }

  exportAudit() {
    if (!this.auditSchool) {
      return;
    }
    // export always covers the full month, not just the visible page; large
    // schools exceed a single response, so the export pages through everything
    const size = 1000;
    const schoolId = this.auditSchool.schoolId;
    this.subscriptionsRest
      .getAuditReport(this.month, schoolId, Pageable.of(0, size, null))
      .pipe(
        expand((page) =>
          page.last
            ? EMPTY
            : this.subscriptionsRest.getAuditReport(
                this.month, schoolId, Pageable.of(page.number + 1, size, null),
              ),
        ),
        map((page) => page.content),
        reduce(
          (all, rows) => all.concat(rows),
          [] as SubscriptionEvent[],
        ),
      )
      .subscribe((events) => {
        if (!events.length) {
          return;
        }
        const rows = events.map((event) => ({
          school: event.schoolName,
          person: ManagerUtils.getName(event.person),
          product: productTypeLabel(event.productType),
          event: subscriptionEventLabel(event),
          source: event.eventSource,
          billingMonth: event.billingMonth,
          effectiveDate: formatDate(event.effectiveDate, 'yyyy-MM-dd', 'en'),
          status: event.subscriptionStatus,
          notes: event.notes ?? '',
        }));
        this.exportExcel.exportToExcel(
          rows,
          `subscriptions-audit-${this.auditSchool?.schoolId}-${this.month}`,
          'default',
        );
      });
  }

  private sumUp(
    reports: SubscriptionInvoiceReport[],
  ): SubscriptionInvoiceReport | null {
    if (!reports.length) {
      return null;
    }
    const totals: SubscriptionInvoiceReport = {
      schoolId: null,
      schoolName: 'Total',
      newStudentRegistrations: 0,
      renewedStudentRegistrations: 0,
      newTeacherRegistrations: 0,
      renewedTeacherRegistrations: 0,
      newTeacherSubscriptions: 0,
      renewedTeacherSubscriptions: 0,
      manualRenewedStudentRegistrations: 0,
      automaticRenewedStudentRegistrations: 0,
      payableStudentRegistrations: 0,
      manualRenewedTeacherRegistrations: 0,
      automaticRenewedTeacherRegistrations: 0,
      payableTeacherRegistrations: 0,
      manualRenewedTeacherSubscriptions: 0,
      automaticRenewedTeacherSubscriptions: 0,
      payableTeacherSubscriptions: 0,
      payableStudents: 0,
      payableTeachers: 0,
      manualBillableEvents: 0,
      automaticRenewals: 0,
      stoppedSubscriptions: 0,
      expiredSubscriptions: 0,
      totalPayable: 0,
    };
    for (const report of reports) {
      totals.newStudentRegistrations += report.newStudentRegistrations;
      totals.renewedStudentRegistrations += report.renewedStudentRegistrations;
      totals.newTeacherRegistrations += report.newTeacherRegistrations;
      totals.renewedTeacherRegistrations += report.renewedTeacherRegistrations;
      totals.newTeacherSubscriptions += report.newTeacherSubscriptions;
      totals.renewedTeacherSubscriptions += report.renewedTeacherSubscriptions;
      totals.manualRenewedStudentRegistrations += report.manualRenewedStudentRegistrations;
      totals.automaticRenewedStudentRegistrations += report.automaticRenewedStudentRegistrations;
      totals.payableStudentRegistrations += report.payableStudentRegistrations;
      totals.manualRenewedTeacherRegistrations += report.manualRenewedTeacherRegistrations;
      totals.automaticRenewedTeacherRegistrations += report.automaticRenewedTeacherRegistrations;
      totals.payableTeacherRegistrations += report.payableTeacherRegistrations;
      totals.manualRenewedTeacherSubscriptions += report.manualRenewedTeacherSubscriptions;
      totals.automaticRenewedTeacherSubscriptions += report.automaticRenewedTeacherSubscriptions;
      totals.payableTeacherSubscriptions += report.payableTeacherSubscriptions;
      totals.payableStudents += report.payableStudents;
      totals.payableTeachers += report.payableTeachers;
      totals.manualBillableEvents += report.manualBillableEvents;
      totals.automaticRenewals += report.automaticRenewals;
      totals.stoppedSubscriptions += report.stoppedSubscriptions;
      totals.expiredSubscriptions += report.expiredSubscriptions;
      totals.totalPayable += report.totalPayable;
    }
    return totals;
  }
}
